<template>
  <el-dialog
    title="Partner presentations"
    :visible.sync="syncedVisible"
    :close-on-click-modal="false"
    width="520px"
    destroy-on-close
    @close="reset"
  >
    <div class="agency-presentations">
      <div v-if="loading" class="agency-presentations__loading">Loading...</div>

      <ul v-else-if="urls.length" class="agency-presentations__list">
        <li v-for="url in urls" :key="url" class="agency-presentations__item">
          <a :href="url" target="_blank" rel="noopener" class="agency-presentations__link">{{ url }}</a>
          <button
            v-if="canManageAgencies"
            type="button"
            class="agency-presentations__remove"
            :disabled="removing === url"
            title="Remove"
            @click="remove(url)"
          >
            &times;
          </button>
        </li>
      </ul>

      <div v-else class="agency-presentations__empty">No presentations uploaded yet</div>

      <label v-if="canManageAgencies" class="agency-presentations__field">
        <span>Add file(s) — PDF or PPTX</span>
        <input type="file" accept=".pdf,.pptx" multiple @change="onFiles" />
      </label>
      <small v-if="pendingFiles.length" class="text-success d-block mt-1">
        {{ pendingFiles.map((f) => f.name).join(', ') }}
      </small>
    </div>

    <div slot="footer" class="agency-presentations__footer">
      <base-button type="default" @click="close">Close</base-button>
      <base-button v-if="canManageAgencies" type="primary" :disabled="!pendingFiles.length || uploading" @click="upload">
        {{ uploading ? 'Uploading...' : 'Upload' }}
      </base-button>
    </div>
  </el-dialog>
</template>

<script>
import agencyAccess from '@/components/Agencies/agencyAccess';
import { mapActions } from 'vuex';

export default {
  mixins: [agencyAccess],
  props: {
    visible: { type: Boolean, required: true },
    agencyId: { type: Number, required: true },
  },
  data: () => ({
    urls: [],
    pendingFiles: [],
    loading: false,
    uploading: false,
    removing: null,
  }),
  computed: {
    syncedVisible: {
      get() {
        return this.visible;
      },
      set(v) {
        this.$emit('update:visible', v);
      },
    },
  },
  watch: {
    visible: {
      immediate: true,
      handler(isVisible) {
        if (isVisible) this.load();
      },
    },
  },
  methods: {
    ...mapActions('agencies', ['getPresentationUrls', 'uploadPresentations', 'removePresentation']),
    onFiles(e) {
      if (!this.canManageAgencies) return;
      this.pendingFiles = Array.from(e.target.files || []);
    },
    close() {
      this.syncedVisible = false;
    },
    reset() {
      this.urls = [];
      this.pendingFiles = [];
      this.loading = false;
      this.uploading = false;
      this.removing = null;
    },
    async load() {
      this.loading = true;
      try {
        this.urls = await this.getPresentationUrls(this.agencyId);
      } catch (e) {
        this.$notify({ type: 'danger', message: 'Failed to load presentations' });
      } finally {
        this.loading = false;
      }
    },
    async upload() {
      if (!this.canManageAgencies) return;
      if (!this.pendingFiles.length) return;
      this.uploading = true;
      try {
        await this.uploadPresentations({ agencyId: this.agencyId, files: this.pendingFiles });
        this.pendingFiles = [];
        // re-fetch via a fresh GET (separate from the upload response) to verify the URLs are actually persisted
        this.urls = await this.getPresentationUrls(this.agencyId);
        this.$notify({ type: 'success', title: 'Success', message: 'Presentation(s) uploaded — check the link(s) below' });
      } catch (e) {
        this.$notify({ type: 'danger', message: e?.response?.data?.error?.code || 'Upload failed' });
      } finally {
        this.uploading = false;
      }
    },
    async remove(url) {
      if (!this.canManageAgencies) return;
      this.removing = url;
      try {
        this.urls = await this.removePresentation({ agencyId: this.agencyId, url });
      } catch (e) {
        this.$notify({ type: 'danger', message: 'Failed to remove presentation' });
      } finally {
        this.removing = null;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.agency-presentations {
  display: flex;
  flex-direction: column;
  gap: 12px;

  &__loading,
  &__empty {
    color: #888;
    font-size: 13px;
  }

  &__list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  &__item {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 8px;
    padding: 6px 10px;
    background: #f4f8fb;
    border-radius: 6px;
  }

  &__link {
    word-break: break-all;
    font-size: 12px;
    font-family: monospace;
  }

  &__remove {
    border: none;
    background: transparent;
    color: #e3342f;
    cursor: pointer;
    font-size: 16px;
    line-height: 1;
  }

  &__field {
    display: flex;
    flex-direction: column;
    gap: 4px;
    span {
      font-size: 13px;
      color: #555;
    }
  }

  &__footer {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
  }
}
</style>
